"use client";

import type { CSSProperties } from "react";
import { useId } from "react";

/** Brand accent (matches wordmark “x” icon). */
const BRAND_TEAL = "#1ca4b1";

type HourglassMarkProps = {
  /** Pixel width of the mark (height follows hourglass aspect ratio). */
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** Accessible label; empty string = decorative (aria-hidden). */
  title?: string;
  /** Triangle fill (defaults to brand teal). Ignored when `premiumDark` is true. */
  color?: string;
  /**
   * Jewel-tone cyan gradients + specular edge — for dark-mode vector wordmark only.
   * Uses unique gradient IDs via `useId` so multiple marks on a page never clash.
   */
  premiumDark?: boolean;
  /** Animated sand-flow: top chamber drains, bottom fills, grain at the waist. */
  withSand?: boolean;
};

/**
 * Two triangles meeting at the waist — classic hourglass (tips touch at centre).
 * Primary app icon mark (favicon, PWA, OAuth, chrome).
 */
export function HourglassMark({
  size = 44,
  className,
  style,
  title = "",
  color = BRAND_TEAL,
  premiumDark = false,
  withSand = false,
}: HourglassMarkProps) {
  const h = Math.round((size * 48) / 40);
  const decorative = title === "";
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const gTop = `ed-hg-top-${uid}`;
  const gBot = `ed-hg-bot-${uid}`;

  if (premiumDark) {
    return (
      <svg
        width={size}
        height={h}
        viewBox="0 0 40 48"
        className={className}
        aria-hidden={decorative ? true : undefined}
        aria-label={decorative ? undefined : title}
        style={{ flexShrink: 0, display: "block", ...style }}
      >
        {!decorative ? <title>{title}</title> : null}
        <defs>
          <linearGradient id={gTop} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ecfeff" />
            <stop offset="35%" stopColor="#67e8f9" />
            <stop offset="72%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
          <linearGradient id={gBot} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="45%" stopColor="#0891b2" />
            <stop offset="100%" stopColor="#155e75" />
          </linearGradient>
        </defs>
        <polygon points="2,4 38,4 20,24" fill={`url(#${gTop})`} className={withSand ? "ed-hg-sand-top" : undefined} />
        <polygon points="20,24 38,44 2,44" fill={`url(#${gBot})`} className={withSand ? "ed-hg-sand-bot" : undefined} />
        <polygon
          points="2,4 38,4 20,24"
          fill="none"
          stroke="rgba(255,255,255,0.35)"
          strokeWidth="0.75"
          strokeLinejoin="round"
        />
        <polygon
          points="20,24 38,44 2,44"
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="0.5"
          strokeLinejoin="round"
        />
        {withSand ? (
          <ellipse cx="20" cy="25" rx="1.5" ry="1" fill="#67e8f9" className="ed-hg-sand-stream">
            <animate
              attributeName="cy"
              values="24;29;24"
              dur="0.55s"
              repeatCount="indefinite"
              calcMode="spline"
              keySplines="0.4 0 0.6 1;0.4 0 0.6 1"
            />
            <animate
              attributeName="ry"
              values="1;1.8;1"
              dur="0.55s"
              repeatCount="indefinite"
              calcMode="spline"
              keySplines="0.4 0 0.6 1;0.4 0 0.6 1"
            />
            <animate
              attributeName="opacity"
              values="0.85;0;0.85"
              dur="0.55s"
              repeatCount="indefinite"
              calcMode="spline"
              keySplines="0.4 0 0.6 1;0.4 0 0.6 1"
            />
          </ellipse>
        ) : null}
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={h}
      viewBox="0 0 40 48"
      className={className}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : title}
      style={{ flexShrink: 0, display: "block", ...style }}
    >
      {!decorative ? <title>{title}</title> : null}
      <polygon points="2,4 38,4 20,24" fill={color} className={withSand ? "ed-hg-sand-top" : undefined} />
      <polygon points="20,24 38,44 2,44" fill={color} className={withSand ? "ed-hg-sand-bot" : undefined} />
      {withSand ? (
        <ellipse cx="20" cy="25" rx="1.5" ry="1" fill={color} className="ed-hg-sand-stream">
          <animate
            attributeName="cy"
            values="24;29;24"
            dur="0.55s"
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.4 0 0.6 1;0.4 0 0.6 1"
          />
          <animate
            attributeName="ry"
            values="1;1.8;1"
            dur="0.55s"
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.4 0 0.6 1;0.4 0 0.6 1"
          />
          <animate
            attributeName="opacity"
            values="0.85;0;0.85"
            dur="0.55s"
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.4 0 0.6 1;0.4 0 0.6 1"
          />
        </ellipse>
      ) : null}
    </svg>
  );
}
